// src/utils/ohlcvUtils.js

/**
 * Parses and processes OHLCV data from GeckoTerminal API
 * @param {Array} ohlcvList - Raw OHLCV data from the API
 * @returns {Array} - Formatted OHLCV data ready for charting
 */
export const parseOHLCVData = (ohlcvList) => {
  if (!ohlcvList || !Array.isArray(ohlcvList)) {
    return [];
  }
  
  return ohlcvList.map(item => ({
    timestamp: item[0] * 1000, // Convert to milliseconds for JavaScript Date
    open: item[1],
    high: item[2],
    low: item[3],
    close: item[4],
    volume: item[5]
  }));
};

/**
 * Filters OHLCV data based on a timeframe
 * @param {Array} data - Parsed OHLCV data
 * @param {string} timeframe - Timeframe to filter by ('1h', '24h', '1w', '1m')
 * @returns {Array} - Filtered OHLCV data
 */
export const filterDataByTimeframe = (data, timeframe) => {
  if (!data || !Array.isArray(data)) {
    return [];
  }
  
  const now = Date.now();
  
  switch (timeframe) {
    case '1h':
      return data.filter(d => now - d.timestamp <= 3600000); // 1 hour in ms
    case '24h':
      return data.filter(d => now - d.timestamp <= 86400000); // 24 hours in ms
    case '1w':
      return data.filter(d => now - d.timestamp <= 604800000); // 1 week in ms
    case '1m':
      return data.filter(d => now - d.timestamp <= 2592000000); // 30 days in ms
    default:
      return data;
  }
};

/**
 * Calculates price change percentage
 * @param {Array} data - OHLCV data
 * @returns {number} - Price change percentage
 */
export const calculatePriceChange = (data) => {
  if (!data || data.length < 2) {
    return 0;
  }
  
  const firstPrice = data[0].close;
  const lastPrice = data[data.length - 1].close;
  
  return ((lastPrice - firstPrice) / firstPrice) * 100;
};

/**
 * Determines chart color based on price trend
 * @param {Array} data - OHLCV data
 * @returns {string} - CSS color variable or hex color
 */
export const getChartColor = (data) => {
  if (!data || data.length < 2) {
    return "#8884d8"; // Default purple
  }
  
  const priceChange = calculatePriceChange(data);
  
  return priceChange >= 0 ? "var(--success)" : "var(--error)";
};

/**
 * Formats date for chart axis based on timeframe
 * @param {number} timestamp - Timestamp in milliseconds
 * @param {string} timeframe - Selected timeframe
 * @returns {string} - Formatted date/time string
 */
export const formatAxisDate = (timestamp, timeframe) => {
  const date = new Date(timestamp);
  
  switch (timeframe) {
    case '1h':
    case '24h':
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case '1w':
    case '1m':
      return `${date.getMonth() + 1}/${date.getDate()}`;
    default:
      return date.toLocaleTimeString();
  }
};

/**
 * Gets all API parameters needed for a specific timeframe
 * @param {string} timeframe - Selected timeframe ('1h', '24h', '1w', '1m')
 * @returns {Object} - API parameters
 */
export const getTimeframeParams = (timeframe) => {
  switch (timeframe) {
    case '1h':
      return { timeframe: 'minute', aggregate: 1 };
    case '24h':
      return { timeframe: 'hour', aggregate: 1 };
    case '1w':
      return { timeframe: 'hour', aggregate: 4 };
    case '1m':
      return { timeframe: 'day', aggregate: 1 };
    default:
      return { timeframe: 'hour', aggregate: 1 };
  }
};

/**
 * Creates the full GeckoTerminal API URL for OHLCV data
 * @param {string} network - Network ID (e.g., 'bsc', 'eth')
 * @param {string} pool - Pool address
 * @param {string} timeframe - Selected timeframe ('1h', '24h', '1w', '1m')
 * @returns {string} - Complete API URL
 */
export const buildOHLCVApiUrl = (network, pool, timeframe) => {
  const params = getTimeframeParams(timeframe);
  return `https://api.geckoterminal.com/api/v2/networks/${network}/pools/${pool}/ohlcv/${params.timeframe}?aggregate=${params.aggregate}&currency=usd`;
};